import { Divider, Tab, Tabs } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import MultiSelectExtension from "components/Shared/MultiSelectExtension/MultiSelectExtension";
import EquipmentPieceOptions from "modules/equipmentPieces/EquipmentPieceOptions";
import Create from "modules/OrganizationEquipmentPieces/Create";
import { CreateEquipmentPieceInput } from "generated/graphql";
import useCreateEquipmentPiece from "hooks/organization/useCreateEquipmentPiece";
import { useCallback, useState } from "react";
import { draftColors } from "./draftDesignTokens";
import { drawerTabStyles } from "./drawerTabStyles";

type AddEquipmentDrawerProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (ids: string[]) => Promise<void>;
};

export default function AddEquipmentDrawer({ open, onClose, onAdd }: AddEquipmentDrawerProps) {
  const [createEquipmentPiece, { loading, isSuccess, getData }] = useCreateEquipmentPiece();
  const [eps, setEps] = useState<string[]>([]);
  const [equipmentPieceId, setEquipmentPieceId] = useState<string | null>(null);
  const [openTab, setOpenTab] = useState(0);

  const handleCreate = useCallback(
    async (values: CreateEquipmentPieceInput) => {
      const formattedInput = {
        ...values,
        rate: Math.floor(values.rate * 100),
        fuelSurcharge: Math.floor(values.fuelSurcharge * 100),
      };
      const result = await createEquipmentPiece({ variables: { input: formattedInput } });
      const data = getData(result);
      if (isSuccess(result.data)) {
        await onAdd([data.id]);
        onClose();
      }
      return { success: isSuccess(result.data), data };
    },
    [createEquipmentPiece, getData, isSuccess, onAdd, onClose]
  );

  const handleAdd = useCallback(async () => {
    if (eps.length > 0) {
      await onAdd(eps);
      setEps([]);
      onClose();
    }
  }, [eps, onAdd, onClose]);

  return (
    <UiSideModal open={open} onClose={onClose} title="Add Equipment" width="598px">
      <MDBox display="flex" flexDirection="column" height="100%">
        <MDBox px="24px" pt="8px">
          <Tabs value={openTab} onChange={(_, v) => setOpenTab(v)} sx={drawerTabStyles}>
            <Tab label="Select existing" />
            <Tab label="Create new" />
          </Tabs>
        </MDBox>

        <Divider sx={{ borderColor: draftColors.border, m: 0 }} />

        <MDBox p="24px" display="flex" flexDirection="column" gap="16px" flex={1}>
          {openTab === 0 && (
            <EquipmentPieceOptions>
              {({
                equipmentPiecesById,
                equipmentPieceOptions,
                loading: optionsLoading,
                search,
                pagination,
              }) => (
                <MDBox>
                  <UiTypography uiVariant="heading-sm" sx={{ mb: "12px" }}>
                    Equipment Piece(s)
                  </UiTypography>
                  <MultiSelectExtension
                    tags={eps}
                    setTags={setEps}
                    options={equipmentPieceOptions}
                    onChange={(value) => setEquipmentPieceId(value as string)}
                    loading={optionsLoading}
                    pagination={pagination}
                    search={search}
                    id={equipmentPieceId}
                    label="Equipment Piece"
                    nameCheckFn={equipmentPiecesById}
                  />
                  <MDBox display="flex" justifyContent="flex-end">
                    <UiButton
                      uiVariant="primary-blue"
                      onClick={handleAdd}
                      disabled={eps.length < 1}
                    >
                      Add selected
                    </UiButton>
                  </MDBox>
                </MDBox>
              )}
            </EquipmentPieceOptions>
          )}

          {openTab === 1 && <Create onSubmit={handleCreate} loading={loading} />}
        </MDBox>
      </MDBox>
    </UiSideModal>
  );
}
